
import React, { memo, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { translate } from '@components/i18n';
import { Table } from '@components/table';
import { ItemResponse } from '@models/item';
import { authorizedClientSelector, exportAuthorizedClients, fetchAuthorizedClients } from '@redux/slices/authorized-clients';
import { Col, Form, Row } from 'antd';
import { SorterResult, FilterValue } from 'antd/lib/table/interface';
import { ModalContext } from 'src/contexts/modal-context';
import { columns } from '../constants';
import { EditModal } from './edit-modal';
import { Input } from '@components/input';
import { SearchOutlined } from '@ant-design/icons';
import { ExportButton } from '@styles/components/button';
import { AuthorizedClientsParams } from '@models/authorized-clients';

export const Body: React.FC = memo(() => {
    const dispatch = useDispatch()
    const data = useSelector(authorizedClientSelector);
    const modalContext = useContext(ModalContext);
    const [form] = Form.useForm()

    const edit = (
        data: ItemResponse,
        _: number,
        event: React.MouseEvent<HTMLElement, MouseEvent>,
    ): void => {
        if (
            !`${event.target}`.includes('SVG') &&
            !`${event.target}`.includes('Button') &&
            !`${event.target}`.includes('Span')
        )
            modalContext.setIsOpen(
                true,
                <EditModal data={data} key={`editing-item-${data.numero}`} />,
                {
                    title: `${translate('general.editItem')} - ${data.numero}`,
                    width: '40%',
                },
            );
    };

    const loadData = (
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        pagination: any,
        teste: Record<string, FilterValue>,
        sorter: SorterResult<unknown>,
    ): void => {
        const params: AuthorizedClientsParams = {
            query: form.getFieldValue("filterString")?.trim() || '',
            page: pagination.current - 1,
            size: pagination.pageSize,
        }

        if (sorter.order && sorter.field) {
            Object.assign(params, {
                sort: `${sorter.field},${sorter.order.substring(0, 3)}`
            })
        }
        form.setFieldsValue({
            page: params.page,
            size: params.size,
        })

        dispatch(fetchAuthorizedClients(params));
    };

    const add = (): void => {
        modalContext.setIsOpen(true, <EditModal />, {
            title: translate('general.registerItem'),
            width: '40%',
        });
    };

    const onExport = (): void => {
        dispatch(exportAuthorizedClients());
    };

    const search = (): void => {
        dispatch(
            fetchAuthorizedClients({
                query: form.getFieldValue("filterString")?.trim() || '',
                size: data?.response?.size || 20,
                page: 0,
            }),
        );
    };

    return (
        <Col>
            <Table
                columns={columns}
                rowKey={(record: ItemResponse) => `${record.id}`}
                actions={[
                    <Row justify="end" gutter={16} align="middle">
                        <Col>
                            <ExportButton onClick={add}>
                                {translate('forms.buttons.addNew')}
                            </ExportButton>
                        </Col>
                        <Col>
                            <ExportButton onClick={onExport}>
                                {translate('general.export')}
                            </ExportButton>
                        </Col>
                    </Row>
                ]}
                filters={[
                    <Form.Item wrapperCol={{ span: 24 }}>
                        <Input
                            placeholder={translate(
                                'forms.placeholders.search',
                            )}
                            allowClear
                            suffix={
                                <div
                                    onClick={search}
                                    style={{
                                        cursor: 'pointer',
                                    }}
                                >
                                    <SearchOutlined />
                                </div>
                            }
                            onKeyUp={(e) => {
                                if (
                                    e.code === `Enter` ||
                                    e.keyCode === 13
                                ) {
                                    search();
                                }
                            }}
                            onChange={(e) =>
                                form.setFieldsValue({ filterString: e.target.value })
                            }
                        />
                    </Form.Item>,
                ]}
                dataSource={data?.response?.content || []}
                pagination={{
                    current: data?.response?.number ? data.response.number + 1 : 1,
                    pageSize: data.response?.size || 10,
                    total: data?.response?.totalElements || 0
                }}
                pointer
                onRowClick={edit}
                scroll={{ y: 'calc(100vh - 380px)' }}
                onChange={loadData}
                isLoading={data?.isFetching}
            />
        </Col>
    );
});
